import { useGetTourOfferSummary } from '~/hooks/apis/tours/useTourOffers';

import { Bundle } from '~/types/bundles/bundle';

import { regionName } from './regions';
import useBundleDepartureChecks from './useBundleDepartureChecks';
import useBundleRegions from './useBundleRegions';

export type BuilderStep = 'build' | 'departures' | 'approval' | 'content' | 'prices';

export const BUILDER_STEPS: ReadonlyArray<{ key: BuilderStep; label: string }> = [
  { key: 'build', label: 'Tour & hotel' },
  { key: 'departures', label: 'Departures' },
  { key: 'approval', label: 'Approval' },
  { key: 'content', label: 'Content' },
  { key: 'prices', label: 'Prices' },
];

export type CheckState = 'done' | 'todo' | 'warning' | 'checking';

export interface ReadinessCheck {
  key: string;
  label: string;
  detail?: string;
  state: CheckState;
  /** Whether approval waits on it. A warning still lets the bundle be approved. */
  isBlocking: boolean;
  step: BuilderStep;
}

const pluralise = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/** Everything the bundle needs before approval, and where each is fixed. */
export default function useBundleReadiness(bundle: Bundle) {
  const departureChecks = useBundleDepartureChecks(bundle);
  const regions = useBundleRegions(bundle);
  const { tour, hotels, departures, isChecking, checkError, blocked } = departureChecks;
  const { data: tourOffer } = useGetTourOfferSummary(tour?.tour_offer_id);

  const isApproved = bundle.status === 'approved';
  const hasLtle = !!tourOffer?.isLtle;

  const checks: Array<ReadinessCheck> = [
    {
      key: 'tour',
      label: 'Base tour added',
      state: tour ? 'done' : 'todo',
      isBlocking: true,
      step: 'build',
    },
    {
      key: 'hotel',
      label: 'Flash hotel added',
      state: hotels.length > 0 ? 'done' : 'todo',
      isBlocking: true,
      step: 'build',
    },
    {
      key: 'regions',
      label: 'Tour and hotel share a region',
      detail: regions.error
        ? "The tour or hotel couldn't be loaded. Reload to try again."
        : regions.shared.length > 0
          ? `Sells in ${regions.shared.map(regionName).join(', ')}`
          : regions.hasTour && regions.hasHotel && !regions.isLoading
            ? 'The tour is not scheduled anywhere the hotel is on sale'
            : undefined,
      state:
        !regions.hasTour || !regions.hasHotel
          ? 'todo'
          : regions.isLoading
            ? 'checking'
            : regions.error
              ? 'warning'
              : regions.shared.length > 0
                ? 'done'
                : 'todo',
      isBlocking: true,
      step: 'build',
    },
    {
      key: 'departures',
      label: 'Departures chosen',
      detail: departures.length > 0 ? pluralise(departures.length, 'departure') : undefined,
      state: departures.length > 0 ? 'done' : 'todo',
      isBlocking: true,
      step: 'departures',
    },
    {
      key: 'availability',
      label: 'Every departure can be sold',
      detail: checkError
        ? "Availability couldn't be checked"
        : blocked.length > 0
          ? `${pluralise(blocked.length, 'departure')} can't be sold as a single and a double`
          : undefined,
      state:
        departures.length === 0
          ? 'todo'
          : isChecking
            ? 'checking'
            : checkError || blocked.length > 0
              ? 'warning'
              : 'done',
      isBlocking: false,
      step: 'departures',
    },
  ];

  const blockers = checks.filter((check) => check.isBlocking && check.state !== 'done');

  const isStepComplete: Record<BuilderStep, boolean> = {
    build: checks.filter((check) => check.step === 'build').every((check) => check.state === 'done'),
    departures: checks.filter((check) => check.step === 'departures').every((check) => check.state === 'done'),
    approval: isApproved,
    content: isApproved && hasLtle,
    prices: isApproved && departures.length > 0,
  };

  // The step that holds the first thing left to do.
  const nextStep: BuilderStep = isApproved ? (hasLtle ? 'content' : 'approval') : (blockers[0]?.step ?? 'approval');

  return {
    ...departureChecks,
    regions,
    checks,
    blockers,
    isApproved,
    hasLtle,
    isStepComplete,
    nextStep,
  };
}
